import { doseIndex, metFor, sessionKeyFor } from "./plan";
import { buildQueue, dayQueue, sessionPct, stepSecs } from "./queue";
import type { KayaState, QueueStep, SessionKey, StepBreakdown,
  StepSample, TreadmillPhase } from "./types";

/** Latest logged weight, falling back to the profile's starting weight. */
export function curWeight(S: KayaState): number {
  const ms = S.measures.filter((m) => m.w).sort((a, b) => (a.date < b.date ? -1 : 1));
  return ms.length ? ms[ms.length - 1].w! : S.profile.startWeight;
}

export function bmi(S: KayaState, w: number): number {
  const h = S.profile.heightCm / 100;
  return w / (h * h);
}

/** MET × kg × hours, scaled by how much of the session was actually completed. `queue` prices a
 *  day by the steps it ran (ADR-050); without one, today's queue. */
export function sessionKcal(S: KayaState, key: SessionKey, fracDone?: number, queue?: QueueStep[]): number {
  const q = queue ?? buildQueue(S, key, doseIndex(S, key));
  const secs = q.reduce((a, st) => a + stepSecs(st), 0);
  return Math.round(
    metFor(key) * curWeight(S) * (secs / 3600) * (fracDone === undefined ? 1 : fracDone)
  );
}

/** Walking MET by speed band, plus a bump for incline. */
export function walkMet(speed: number, inc?: number): number {
  const m =
    speed < 4 ? 2.8 : speed < 5 ? 3.3 : speed < 5.6 ? 3.8 : speed < 6.4 ? 4.5 : speed < 7.2 ? 5.0 : 6.0;
  return m + (inc || 0) * 0.6;
}

export function phaseKcal(S: KayaState, ph: TreadmillPhase): number {
  return walkMet(ph.s, ph.inc) * curWeight(S) * (ph.secs / 3600);
}

export function phaseKm(ph: TreadmillPhase): number {
  return (ph.s * ph.secs) / 3600;
}

/** The textbook constant: 0.415 × height. A population average, and the starting point only. */
export function heightStride(S: KayaState): number {
  return (0.415 * S.profile.heightCm) / 100;
}

/**
 * Stride length in metres — measured if we have measured it, estimated from height if not.
 *
 * A treadmill session is the one situation that can calibrate this honestly: the distance is
 * known exactly (speed × time, set by the machine) and the phone counts the steps, so the stride
 * is simply one divided by the other. No other activity gives both numbers.
 */
export function strideMeters(S: KayaState): number {
  const learned = S.profile.strideM;
  return typeof learned === "number" && learned > 0 ? learned : heightStride(S);
}

export function kmToSteps(S: KayaState, km: number): number {
  return Math.round((km * 1000) / strideMeters(S));
}

/** A walk is only worth learning from if it is long enough and actually measured the walker. */
export const STRIDE_MIN_METRES = 300;
export const STRIDE_MIN_STEPS = 300;
/** Human walking strides live well inside this; anything outside is a measurement artefact. */
export const STRIDE_MIN_M = 0.4;
export const STRIDE_MAX_M = 1.2;

/**
 * Folds one treadmill walk into the learned stride, or returns null if the walk cannot teach us
 * anything. Returning null rather than a best guess is the point — a phone left on the console
 * measures almost no steps, which would imply an absurd stride and wreck the calibration.
 *
 * Short walks are rejected for the same reason a pedometer asks you to walk 100 m before it
 * trusts itself: the first and last few steps of a session are ragged, and over 50 steps that
 * noise dominates.
 */
export function calibrateStride(
  S: KayaState,
  km: number,
  measuredSteps: number
): { strideM: number; strideSamples: number } | null {
  const metres = km * 1000;
  if (metres < STRIDE_MIN_METRES || measuredSteps < STRIDE_MIN_STEPS) return null;

  const candidate = metres / measuredSteps;
  if (candidate < STRIDE_MIN_M || candidate > STRIDE_MAX_M) return null;

  const prev = S.profile.strideM;
  const n = S.profile.strideSamples || 0;
  // Exponential moving average: a new walk moves the figure but cannot yank it. The first
  // measurement is taken whole, since there is nothing yet to average against.
  const strideM = typeof prev === "number" && prev > 0 && n > 0 ? prev * 0.7 + candidate * 0.3 : candidate;
  return { strideM: Math.round(strideM * 1000) / 1000, strideSamples: n + 1 };
}

export function dayKcal(S: KayaState, ds: string): number {
  const L = S.logs[ds];
  if (!L) return 0;
  let k = L.extraKcal || 0;
  const key = L.sessionKey || sessionKeyFor(S, ds);
  const pct = sessionPct(S, ds, key);
  if (L.kcal) k += L.kcal;
  else if (pct) k += sessionKcal(S, key, pct, dayQueue(S, ds, key).queue ?? undefined);
  if (L.treadmill && L.treadmill.kcal) k += L.treadmill.kcal;
  // Free walks are real work and belong in the day's burn. Unlike their steps — which the phone
  // has already counted into the daily total — their calories are not represented anywhere else.
  (L.walks || []).forEach((w) => {
    k += w.kcal || 0;
  });
  return Math.round(k);
}

/**
 * Reconciles the places a step count can come from, without ever double-counting the
 * treadmill walk.
 *
 * The awkward case: a phone left on the treadmill console counts almost nothing for a
 * 30-minute walk, so the speed-based estimate has to be added. A phone in a pocket counts
 * the whole walk, so adding the estimate would count it twice. A daily total alone cannot
 * tell the two apart, which is why the treadmill screen asks once and remembers the answer.
 *
 * With no answer recorded we take the larger of the two readings — conservative, and it
 * never inflates a number the user might act on.
 */
/**
 * How many steps the phone counted between two moments, from its cumulative samples.
 *
 * Samples are day-to-date totals, so the answer is the difference between the last reading at or
 * before each end of the window. Returns null rather than 0 when the window is not actually
 * covered — an unmeasured session and a session measured as zero mean opposite things, and
 * conflating them would silently claim the phone was on the console.
 */
export function measuredInWindow(
  samples: StepSample[],
  fromMs: number,
  toMs: number
): number | null {
  if (!samples.length || !(toMs > fromMs)) return null;

  const sorted = [...samples].sort((a, b) => a.t - b.t);
  // The counter resets at midnight, so a window spanning one cannot be differenced.
  if (new Date(fromMs).toDateString() !== new Date(toMs).toDateString()) return null;

  // Any earlier reading will do for a closed window, so the tolerance is open — but a window
  // whose only boundary reading arrived a moment late still has to be measurable.
  const before = baselineAt(sorted, fromMs, Number.POSITIVE_INFINITY);
  let after: StepSample | null = null;
  for (const s of sorted) {
    if (s.t >= toMs && !after) after = s;
  }
  // Without a reading on each side the window is only partly covered, and a partial answer
  // here would understate the walk and then be treated as fact.
  if (!before || !after || after.t < before.t) return null;
  return Math.max(0, after.steps - before.steps);
}

/**
 * How stale a reading may be and still serve as a live session's zero point.
 *
 * The session ping takes one at the moment Start is pressed, so in practice the baseline is
 * seconds old. Falling back to whatever scheduled sample happened to land beforehand would count
 * steps walked in the quarter of an hour *before* the walk began, and report them as part of it.
 */
export const LIVE_BASELINE_TOLERANCE_MS = 3 * 60 * 1000;

/**
 * How *late* the boundary reading may be and still serve as the window's zero point.
 *
 * The ping fires the instant Start is pressed, but the reading it triggers is stamped when the
 * counter app gets round to taking it — launching an activity and waiting for a sensor event,
 * which is a second or two and can be longer on a cold start. That stamp therefore lands just
 * *after* the moment the walk began, so a baseline that insisted on `t <= start` was never
 * satisfied by the one sample taken specifically to be it: the walk screen showed a dash for its
 * whole duration, however long you waited.
 *
 * Accepting a slightly late baseline undercounts by whatever was walked in that gap. That is the
 * safe direction — it can lose a step or two, where the alternative error credits a walk with
 * steps taken before it started.
 */
export const BOUNDARY_GRACE_MS = 90 * 1000;

/**
 * The reading a window should be measured from.
 *
 * Prefers the last reading at or before the window opened. Falls back to one taken just after it,
 * which is what the boundary ping actually produces — see [BOUNDARY_GRACE_MS].
 */
function baselineAt(
  sorted: StepSample[],
  fromMs: number,
  toleranceMs: number
): StepSample | null {
  let before: StepSample | null = null;
  for (const s of sorted) {
    if (s.t <= fromMs) before = s;
    else break;
  }
  if (before && fromMs - before.t <= toleranceMs) return before;
  return sorted.find((s) => s.t > fromMs && s.t - fromMs <= BOUNDARY_GRACE_MS) ?? null;
}

/**
 * Steps counted since a session began, for a session that has not finished yet.
 *
 * `measuredInWindow` needs a reading on each side and so can only speak about a closed window.
 * A walk in progress has no far edge — the newest reading is the far edge, and it moves. Returns
 * null rather than 0 when there is nothing to say: "no measurement yet" and "you have not moved"
 * are different answers and the walk screen shows them differently.
 */
export function measuredSince(
  samples: StepSample[],
  fromMs: number,
  nowMs: number
): number | null {
  if (!samples.length || nowMs < fromMs) return null;
  // The counter resets at midnight, so a window spanning one cannot be differenced.
  if (new Date(fromMs).toDateString() !== new Date(nowMs).toDateString()) return null;

  const sorted = [...samples].sort((a, b) => a.t - b.t);
  const before = baselineAt(sorted, fromMs, LIVE_BASELINE_TOLERANCE_MS);
  if (!before) return null;

  let latest: StepSample | null = null;
  for (const s of sorted) if (s.t >= before.t && s.t <= nowMs) latest = s;

  if (!latest) return null;
  return Math.max(0, latest.steps - before.steps);
}

export function stepBreakdown(S: KayaState, ds: string): StepBreakdown {
  const L = S.logs[ds];
  const empty: StepBreakdown = {
    device: 0,
    treadmillEstimated: 0,
    manual: 0,
    treadmillMeasured: null,
    phoneOnBody: null,
    total: 0,
    basis: "none",
  };
  if (!L) return empty;

  const manual = L.steps || 0;
  const device = L.deviceSteps || 0;
  const treadmillEstimated = L.treadmill?.steps || 0;
  const treadmillMeasured =
    typeof L.treadmill?.measuredSteps === "number" ? L.treadmill.measuredSteps : null;
  const phoneOnBody = L.treadmill
    ? typeof L.treadmillPhoneOnBody === "boolean"
      ? L.treadmillPhoneOnBody
      : null
    : null;

  const base = { device, treadmillEstimated, manual, treadmillMeasured, phoneOnBody };

  if (device) {
    // Measuring the window beats asking about it. Whatever the phone recorded during the walk is
    // already inside the day's total, so only the shortfall against the estimate is missing —
    // which is the whole walk if the phone sat on the console, none of it if it was in a pocket,
    // and the right fraction in between. No question, no max(), no double count.
    if (treadmillMeasured !== null) {
      const missed = Math.max(0, treadmillEstimated - treadmillMeasured);
      return { ...base, total: device + missed, basis: "device-measured" };
    }
    if (phoneOnBody === true)
      return { ...base, total: device, basis: "device-only" };
    if (phoneOnBody === false)
      return { ...base, total: device + treadmillEstimated, basis: "device-plus-estimate" };
    // no treadmill session at all: nothing to double-count
    if (!treadmillEstimated)
      return { ...base, total: Math.max(device, manual), basis: "device-only" };
    return {
      ...base,
      total: Math.max(device, manual + treadmillEstimated),
      basis: "device-conservative",
    };
  }

  const total = manual + treadmillEstimated;
  return {
    ...base,
    total,
    basis: total === 0 ? "none" : manual ? "manual-plus-estimate" : "estimate-only",
  };
}

export function daySteps(S: KayaState, ds: string): number {
  return stepBreakdown(S, ds).total;
}
